import type { FC, SyntheticEvent, ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary";
import { TermsOfService, PasswordInput, isPasswordValid } from "@unauthenticated/shared/components";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { useSubmitOfficeSignUp } from "../useSubmitOfficeSignUp";

import * as styles from "./OfficeSignUpForm.styles";
interface Props {
  invitationToken: string;
  logField: (field: string) => void;
  setSignUpError: (error: string) => void;
}

const OfficeSignUpForm: FC<Props> = ({ logField, invitationToken, setSignUpError }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);
  const [commonPasswordError, setCommonPasswordError] = useState([]);

  const isFormValid = isPasswordValid(password);

  const { onFormSubmit, loading, data } = useSubmitOfficeSignUp({ setSignUpError });

  useEffect(() => {
    if (data) setCommonPasswordError(data?.auth?.officeSignUp?.errors?.fields?.password);
  }, [data]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setCommonPasswordError([]);
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      setPasswordDirty(true);

      return;
    }

    onFormSubmit({ password, invitationToken });
  };

  return (
    <form onSubmit={handleSubmit} css={styles.form} noValidate>
      <PasswordInput
        value={password}
        onChange={handleOnChange}
        wrapperStyles={styles.marginBottom}
        required
        isDirty={passwordDirty}
        onFocus={() => setPasswordDirty(true)}
        onBlur={() => {
          if (isPasswordValid(password)) {
            logField("password");
          }
        }}
        label={t(l.officeSignUp.CreatePassword)}
        errors={commonPasswordError}
        isLoading={loading}
        data-e2e="office-sign-up-password"
      />
      <TermsOfService css={styles.serviceTerms} />
      <Button
        type="submit"
        disabled={!isFormValid}
        isLoading={loading}
        data-e2e="office-sign-up-button"
      >
        {t(l.officeSignUp.ActivateAccount)}
      </Button>
    </form>
  );
};

export { OfficeSignUpForm };
